import React, { useContext, useEffect, useState } from 'react';
import {
	Button,
	ContentLoader,
	ContentWrapper,
	Head,
	RadioButtons,
	Typography,
	withLocalization,
	Wrapper,
} from '@adac-mp/components';
import { createSearchParams, useNavigate, useParams } from 'react-router-dom';
import {
	convertApiDate,
	formatDate,
	getMangeOrderOverviewCollection,
	getOrderUrlParams,
	getResponseErrorNumber,
	getUrlBasePath,
	IOrderListResponse,
	ITypeVignettesOrderManagementFields,
	Locales,
	OfferContext,
	OrderService,
	setRouteLangParam,
	VignetteContext,
} from '@adac-mp/shared';
import { useQuery } from '@apollo/client';

const OrderOverviewPage = () => {
	const navigate = useNavigate();
	const { lang } = useParams();
	const { orderList, updateOrderList } = useContext(OfferContext);
	const { contentfulLabels, updateContentfulLabels } = useContext(VignetteContext);
	const [selectedOrder, setSelectedOrder] = useState<string | undefined>(undefined);

	const { error, data, loading } = useQuery(getMangeOrderOverviewCollection, {
		variables: { lang: lang || 'de' },
	});

	useEffect(() => {
		if (!error) {
			updateContentfulLabels(data?.vignettesOrderManagementCollection.items[0]);
		}
	}, [data]);
	const currentLabels = contentfulLabels as ITypeVignettesOrderManagementFields;

	const handleOrderException = (res: IOrderListResponse) => {
		const errorNumber = getResponseErrorNumber(res.errors);
		if (errorNumber === 3300) {
			navigate(getUrlBasePath(`/link-expired${setRouteLangParam(lang)}`), { replace: true });
		}
	};

	const navigateToOrderManagement = (orderHash: string) => {
		const params = createSearchParams();
		params.set('orderHash', orderHash);
		navigate(getUrlBasePath(`/order-management${setRouteLangParam(lang)}`, params));
	};

	const fetchOrderList = async () => {
		const orderHash = getOrderUrlParams(window.location);
		await OrderService.getOrderList(
			{
				orderHash,
			},
			lang as Locales,
		).then((res) => {
			if (res.code === 200) {
				updateOrderList(res.orderList);
				setSelectedOrder(res.orderList[0].orderHash);
				sessionStorage.setItem('orderOverviewHash', orderHash);
				if (res.orderList.length === 1) {
					navigateToOrderManagement(res.orderList[0].orderHash);
				}
			} else {
				handleOrderException(res);
			}
		});
	};

	useEffect(() => {
		fetchOrderList();
	}, [lang]);

	return (
		<>
			{loading ? (
				<ContentLoader isLoading hasPortal={false} />
			) : (
				<>
					<Wrapper className="min-h-[calc(100vh-126px)] md:min-h-0">
						<Head
							headline={currentLabels?.orderOverviewHeadline || 'Übersicht Ihrer bisherigen Bestellungen'}
							classNameDescription="font-bold"
						/>
						<ContentWrapper className="flex flex-col">
							<div className="rounded bg-white">
								<RadioButtons
									className={`px-4 py-2 border-b border-l-0 border-t-0 border-r-0 border-solid border-b-gray-600 last:border-b-0 last:rounded-b md:py-3`}
									labelClassName="w-full"
									value={selectedOrder}
									setValue={(val) => {
										setSelectedOrder(val);
									}}
									options={orderList.map(({ orderNo, orderHash, orderDetails, reservationDate }) => ({
										id: orderHash,
										label: (
											<div>
												<Typography variant="title" size="lg">
													{orderNo} / {formatDate(convertApiDate(reservationDate))}
												</Typography>
												<br />
												<Typography
													className="block overflow-ellipsis overflow-hidden whitespace-nowrap pr-2"
													size="lg">
													{orderDetails.map((order) => order.serviceCatalogName).join(', ')}
												</Typography>
											</div>
										) as React.ReactNode,
										title: orderNo,
									}))}
								/>
							</div>
							{selectedOrder && (
								<Button
									className="mt-4"
									onClick={() => {
										navigateToOrderManagement(selectedOrder);
									}}>
									{currentLabels?.orderOverviewButton || 'Jetzt Bestellung anzeigen'}
								</Button>
							)}
						</ContentWrapper>
					</Wrapper>
				</>
			)}
		</>
	);
};

export const OrderOverview = withLocalization(OrderOverviewPage);
